import {
  Tooltip as MuiCustomTooltip,
  TooltipProps,
  PopperProps,
} from "@mui/material";
import {
  CSSProperties,
  ReactElement,
  ReactNode,
  RefObject,
  useState,
} from "react";

export interface ITooltipExtraProps {
  children?: ReactNode | string;
  maxWidth?: string | number;
  className?: string;
  dataTestId?: string;
  customStyle?: CSSProperties;
  visible?: boolean;
  divRef?: RefObject<HTMLDivElement>;
  contentClassName?: string;
}

export type Props = Omit<TooltipProps, "children"> & ITooltipExtraProps;

const Tooltip = ({
  title = "",
  children,
  followCursor = true,
  maxWidth = "300px",
  visible = true,
  arrow = true,
  customStyle,
  className,
  dataTestId = "tooltip",
  divRef,
  contentClassName,
  ...restProps
}: Props) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const showFollowCursorTooltip = followCursor && position.x !== 0;

  return (
    <MuiCustomTooltip
      title={visible || showFollowCursorTooltip ? title : ""}
      classes={{ tooltip: className }}
      disableInteractive
      enterTouchDelay={0}
      enterDelay={0}
      PopperProps={
        {
          ...restProps.PopperProps,
          ...(showFollowCursorTooltip && {
            anchorEl: {
              clientHeight: 0,
              clientWidth: 0,
              getBoundingClientRect: () => ({
                top: position.y,
                left: position.x,
                right: position.x,
                bottom: position.y,
                width: 0,
                height: 0,
              }),
            },
          }),
        } as PopperProps
      }
      arrow={arrow}
      componentsProps={{
        popper: {
          "data-testid": dataTestId,
          style: {
            zIndex: 1400,
          },
        } as Partial<PopperProps>,
        tooltip: {
          sx: {
            maxWidth,
            fontSize: "11px",
          },
        },
      }}
      {...(!visible && {
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
      })}
      {...restProps}
      {...(followCursor && {
        onMouseMove: (e) => {
          if (followCursor) {
            setPosition({ x: e.clientX, y: e.clientY });
          }
        },
      })}
    >
      <div
        {...(divRef && { ref: divRef })}
        style={{ ...customStyle }}
        className={contentClassName}
      >
        {children as ReactElement}
      </div>
    </MuiCustomTooltip>
  );
};

export default Tooltip;
